import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { GridCard } from '../Card/Card';
import { CenteredTypographyBox } from './CenteredTypographyBox';
import { FlowDirectionDisplay } from './FlowDirectionDisplay';
import { HorizontalScale } from '../HorizontalScale/HorizontalScale';
import { useTranslation } from 'react-i18next';
import { AGGREGATORS_NS } from './i18n';
import { HubsNames } from '../../stores/useSignalR';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import CardContent from '@mui/material/CardContent';
import Card from '@mui/material/Card';
import { useMessageStore } from '../../stores/useMessageStore';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// Define default state outside component to prevent recreation
const DEFAULT_AGGREGATE_STATE = {
  massTotal: {
    cumulative: null,
    title: 'Total Mass',
    unit: 'MT',
  },
  volumeTotal: {
    cumulative: null,
    title: 'Total Volume',
    unit: 'm³',
  },
  temperature: {
    range: {
      ideal: {
        min: 26,
        max: 44,
      },
      warning: {
        min: 25,
        max: 45,
      },
    },
    instant: null,
    aggregated: null,
    title: 'Temperature',
    unit: '°C',
  },
  massFlowRate: {
    range: {
      ideal: {
        min: 100,
        max: 1300,
      },
      warning: {
        min: 0,
        max: 1400,
      },
    },
    instant: null,
    aggregated: null,
    title: 'Mass Flow Rate',
    unit: 'MT/HR',
  },
  density: {
    range: {
      ideal: {
        min: 550,
        max: 1100,
      },
      warning: {
        min: 500,
        max: 1200,
      },
    },
    instant: null,
    aggregated: null,
    title: 'Density',
    unit: 'kg/m³',
  },
  driveGain: {
    range: {
      ideal: {
        min: 0,
        max: 50,
      },
      warning: {
        min: 0,
        max: 100,
      },
    },
    instant: null,
    aggregated: null,
    title: 'Drive Gain',
    unit: '%',
  },
  pressure: {
    range: {
      ideal: {
        min: 1.2,
        max: 4.8,
      },
      warning: {
        min: 1,
        max: 5,
      },
    },
    instant: null,
    aggregated: null,
    title: 'Pressure',
    unit: 'Bar',
  },
  tubeDamping: {
    range: {
      ideal: {
        min: 0,
        max: 0,
      },
      warning: {
        min: 0, max: 50000,
      },
    },
    instant: null,
    aggregated: null,
    title: 'Tube Damping',
    unit: 'A/m',
  },
};

type AggregatorsProps = {
  isEmerson: boolean;
  showAggregators: boolean;
};

export const Aggregators = (props: AggregatorsProps) => {
  const { breakpoints } = useTheme();
  const { t } = useTranslation([AGGREGATORS_NS]);
  const messages = useMessageStore((state) => state?.messages[HubsNames.aggregateDashboard]);

  const data = useMemo(() => {
    if (!messages?.length) {
      return DEFAULT_AGGREGATE_STATE;
    }
    return messages[messages.length - 1];
  }, [messages]);

  const isMobile = useMediaQuery(breakpoints.down(900));

  const noDataBlock = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: '8px 16px',
        width: '100%',
      }}
    >
      <VisibilityOffIcon sx={{ mr: '16px' }} />
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        {t('components/Aggregators:placeholder')}
      </Typography>
    </Box>
  );

  return (
    <Box component="section" sx={{ marginBottom: '8px' }}>
      {props.showAggregators ? (
        <Grid container>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent sx={{ p: '0px !important' }}>{noDataBlock}</CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={1}>
          <Grid item xs={12} lg={3} sm={6}>
            <GridCard title={t('components/Aggregators:direction')}>
              <FlowDirectionDisplay />
            </GridCard>
          </Grid>
          <Grid item xs={12} lg={3} sm={6}>
            <GridCard title={t('components/Aggregators:massTotal')}>
              <CenteredTypographyBox
                unit={data?.massTotal.unit}
                value={data?.massTotal.cumulative}
              />
            </GridCard>
          </Grid>
          <Grid item xs={12} lg={3} sm={6}>
            <GridCard title={t('components/Aggregators:massFlowRate')}>
              <HorizontalScale
                min={data?.massFlowRate.range.warning.min}
                max={data?.massFlowRate.range.warning.max}
                step={isMobile ? 450 : 350}
                average={data?.massFlowRate.aggregated}
                range={data?.massFlowRate.range}
                instant={data?.massFlowRate.instant}
                unit={data?.massFlowRate.unit}
                showInstant={true}
              />
            </GridCard>
          </Grid>
          <Grid item xs={12} lg={3} sm={6}>
            <GridCard title={t('components/Aggregators:density')}>
              <HorizontalScale
                min={data?.density.range.warning.min}
                max={data?.density.range.warning.max}
                step={200}
                average={data?.density.aggregated}
                range={data?.density.range}
                instant={data?.density.instant}
                unit={data?.density.unit}
                showInstant={true}
              />
            </GridCard>
          </Grid>
          <Grid item xs={12} lg={3} sm={6}>
            {props.isEmerson ? (
              <GridCard title={t('components/Aggregators:driveGainAeration')}>
                <HorizontalScale
                  min={data?.driveGain.range.warning.min}
                  max={data?.driveGain.range.warning.max}
                  step={25}
                  average={data?.driveGain.aggregated}
                  range={data?.driveGain.range}
                  instant={data?.driveGain.instant}
                  unit={data?.driveGain.unit}
                  showInstant={true}
                />
              </GridCard>
            ) : (
              <GridCard title={t('components/Aggregators:tubeDamping')}>
                <HorizontalScale
                  min={data?.tubeDamping.range.warning.min}
                  max={data?.tubeDamping.range.warning.max}
                  step={50000}
                  average={data?.tubeDamping.aggregated}
                  range={data?.tubeDamping.range}
                  instant={data?.tubeDamping.instant}
                  unit={data?.tubeDamping.unit}
                  showInstant={true}
                />
              </GridCard>
            )}
          </Grid>
          <Grid item xs={12} lg={3} sm={6}>
            <GridCard title={t('components/Aggregators:temperature')}>
              <HorizontalScale
                min={data?.temperature.range.warning.min}
                max={data?.temperature.range.warning.max}
                step={5}
                average={data?.temperature.aggregated}
                range={data?.temperature.range}
                instant={data?.temperature.instant}
                unit={data?.temperature.unit}
                showInstant={true}
              />
            </GridCard>
          </Grid>
          <Grid item xs={12} lg={3} sm={6}>
            <GridCard title={t('components/Aggregators:pressure')}>
              <HorizontalScale
                min={data?.pressure.range.warning.min}
                max={data?.pressure.range.warning.max}
                step={1}
                average={data?.pressure.aggregated}
                range={data?.pressure.range}
                instant={data?.pressure.instant}
                unit={data?.pressure.unit}
                showInstant={true}
              />
            </GridCard>
          </Grid>
          <Grid item xs={12} lg={3} sm={6}>
            <GridCard title={t('components/Aggregators:volumeTotal')}>
              <CenteredTypographyBox
                unit={data?.volumeTotal.unit}
                value={data?.volumeTotal.cumulative}
              />
            </GridCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
